import { useVariablesValues } from "@babackapp/variables-registry";
import type { ReturnLineItem } from "@babackapp/return-variables-registry";

/**
 * Typed accessors for the `return.*` variables exposed in the editor's variable
 * dictionary (example values in the editor/preview; resolved real values once
 * production wiring lands). The shared `useVariablesValues` getter is keyed to
 * the tracking `VariableId` union, so we index the underlying record by string
 * here — the single place that cast lives.
 */
export function useReturnVariableValues() {
  const { variablesValues } = useVariablesValues();
  const values = variablesValues as Record<string, unknown>;

  const getReturnItems = (): ReturnLineItem[] => {
    const items = values["return.items"];
    return Array.isArray(items) ? (items as ReturnLineItem[]) : [];
  };

  const getRefund = (): { amount: number | null; currency: string } => ({
    amount:
      typeof values["return.refundAmount"] === "number"
        ? (values["return.refundAmount"] as number)
        : null,
    currency: (values["return.refundCurrency"] as string) ?? "",
  });

  const getShippingInstructions = (): {
    carrierCode?: string;
    hasLogisticLabel?: boolean;
  } => ({
    carrierCode: values["return.carrierCode"] as string | undefined,
    hasLogisticLabel: values["return.hasLogisticLabel"] as boolean | undefined,
  });

  const getRequestDate = (): {
    createdAt?: number;
    requestTimezone?: string;
  } => ({
    createdAt:
      typeof values["return.createdAt"] === "number"
        ? (values["return.createdAt"] as number)
        : undefined,
    requestTimezone: values["return.requestTimezone"] as string | undefined,
  });

  const getDiscountCode = (): { discountCode?: string } => ({
    discountCode: values["return.discountCode"] as string | undefined,
  });

  const getLabelDownload = (): {
    labelUrl?: string;
    hasLogisticLabel?: boolean;
  } => ({
    labelUrl: values["return.labelUrl"] as string | undefined,
    hasLogisticLabel: values["return.hasLogisticLabel"] as boolean | undefined,
  });

  const getReturnStore = (): {
    name?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    zip?: string | null;
    country?: string | null;
    mapUrl?: string | null;
  } | null => {
    const store = values["return.store"];
    if (!store || typeof store !== "object") return null;
    return store as ReturnType<typeof getReturnStore>;
  };

  return {
    getReturnItems,
    getRefund,
    getShippingInstructions,
    getRequestDate,
    getDiscountCode,
    getLabelDownload,
    getReturnStore,
  };
}
